import { useMemo, useState } from "react";
import { t } from "ttag";

import { EmptyState } from "metabase/common/components/EmptyState";
import { Subhead } from "metabase/common/components/type/Subhead";
import { useDebouncedValue } from "metabase/common/hooks/use-debounced-value";
import {
  type PermissionEditorBreadcrumb,
  type PermissionEditorType,
  PermissionsTable,
  type PermissionsTableProps,
} from "metabase/permissions";
import { Icon, Input, Text, TextInput } from "metabase/ui";
import { SEARCH_DEBOUNCE_DURATION } from "metabase/utils/constants";

import { PermissionsEditorBreadcrumbs } from "./PermissionsEditorBreadcrumbs";
import {
  EditorEmptyStateContainer,
  EditorFilterContainer,
  PermissionEditorContentRoot,
  PermissionTableWrapper,
} from "./PermissionsEditorContent.styled";

export type PermissionsEditorContentProps = PermissionEditorType &
  Pick<PermissionsTableProps, "onChange" | "onSelect" | "onAction"> & {
    onBreadcrumbsItemSelect?: (item: PermissionEditorBreadcrumb) => void;
    postHeaderContent?: React.FC;
    preHeaderContent?: React.FC;
  };

export function PermissionsEditorContent({
  title,
  description,
  entities,
  columns,
  filterPlaceholder,
  breadcrumbs,
  onBreadcrumbsItemSelect,
  onChange,
  onSelect,
  onAction,
  postHeaderContent: PostHeaderContent = () => null,
  preHeaderContent: PreHeaderContent = () => null,
}: PermissionsEditorContentProps) {
  const [filter, setFilter] = useState("");
  //this needs to be state to trigger a rerender when it's set
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );

  const debouncedFilter = useDebouncedValue(filter, SEARCH_DEBOUNCE_DURATION);

  const filteredEntities = useMemo(() => {
    const trimmedFilter = debouncedFilter.trim().toLowerCase();

    if (trimmedFilter.length === 0) {
      return null;
    }

    return entities.filter((entity) =>
      entity.name.toLowerCase().includes(trimmedFilter),
    );
  }, [entities, debouncedFilter]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFilter(e.target.value);

  return (
    <PermissionEditorContentRoot data-testid="permissions-editor">
      <PreHeaderContent />
      <Subhead data-testid="permissions-editor-breadcrumbs">
        {title}{" "}
        {breadcrumbs && onBreadcrumbsItemSelect && (
          <PermissionsEditorBreadcrumbs
            items={breadcrumbs}
            onBreadcrumbsItemSelect={onBreadcrumbsItemSelect}
          />
        )}
      </Subhead>

      {description && <Text lh="normal">{description}</Text>}

      <PostHeaderContent />

      <EditorFilterContainer>
        <TextInput
          placeholder={filterPlaceholder}
          onChange={handleFilterChange}
          value={filter}
          leftSection={<Icon name="search" />}
          rightSectionPointerEvents="all"
          rightSection={
            filter.length > 0 ? (
              <Input.ClearButton
                c="text-secondary"
                onClick={() => setFilter("")}
              />
            ) : null
          }
        />
      </EditorFilterContainer>

      <PermissionTableWrapper ref={setScrollElement}>
        <PermissionsTable
          entities={filteredEntities || entities}
          columns={columns}
          onSelect={onSelect}
          onChange={onChange}
          onAction={onAction}
          scrollElement={scrollElement}
          emptyState={
            <EditorEmptyStateContainer>
              <EmptyState message={t`Nothing here`} icon="folder" />
            </EditorEmptyStateContainer>
          }
        />
      </PermissionTableWrapper>
    </PermissionEditorContentRoot>
  );
}
